import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Empty, Select, Space, Table, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import useErpGridPersistence from './useErpGridPersistence';
import {
  buildPivotMatrix,
  formatPivotCell,
  measureKeyOf
} from './pivotUtils';

// ─────────────────────────────────────────────────────────────────
// Glass-i — ErpPivotView (Stage 6A Grid Increment 3)
//
// Optional, lightweight in-browser pivot preview for analytical work.
// Designed for the CURRENT DATASET only (a grid page or a small list
// the analyst already loaded). The component is intentionally NOT
// wired into ErpDataGrid — heavy aggregation belongs on the server,
// and we don't want a master grid silently consuming megabytes of
// browser memory because someone enabled a pivot toggle.
//
// What this component is good for:
//   - Sales by State preview
//   - Order count by Customer Category
//   - Credit Limit total by Status
//   - Quick "what does this slice look like?" exploration during dev
//
// What it is NOT:
//   - A BI engine
//   - A reporting export
//   - A charting widget
//
// Persistence: when `gridId` is supplied, the chosen row / column /
// value / aggregation are stored in localStorage under the existing
// grid persistence namespace (per tenant + user + gridId, with a
// distinct ':pivot' suffix so it never collides with a real grid's
// layout). Row data is NEVER persisted.
// ─────────────────────────────────────────────────────────────────

/** @typedef {import('./gridTypes').GridColumn} GridColumn */

const AGGREGATE_OPTIONS = [
  { value: 'sum',   label: 'Sum' },
  { value: 'count', label: 'Count' },
  { value: 'avg',   label: 'Average' },
  { value: 'min',   label: 'Minimum' },
  { value: 'max',   label: 'Maximum' }
];

// Build the axis-field options from a grid column tree. We surface
// only columns the caller has tagged `pivotable: true` (or those
// already known to be aggregatable). The action column and any
// purely interactive column are skipped.
function collectAxisOptions(columns) {
  const out = [];
  const walk = (cols) => {
    for (const c of cols || []) {
      if (Array.isArray(c.children) && c.children.length) {
        walk(c.children);
        continue;
      }
      if (!c) continue;
      if (c.key === '__erp_actions__') continue;
      if (c.pivotable !== true && c.groupable !== true) continue;
      const key = c.key || c.dataIndex;
      if (!key) continue;
      const dataIndex = c.dataIndex || c.key;
      out.push({
        value: typeof dataIndex === 'string' ? dataIndex : String(dataIndex),
        label: typeof c.title === 'string' ? c.title : String(key)
      });
    }
  };
  walk(columns);
  return out;
}

// Numeric-measure options: columns flagged `aggregatable: true` OR
// already carrying an `aggregate` config. Special pseudo-measure
// "(count of records)" is always offered so users can pivot row
// counts without picking a numeric column.
function collectMeasureOptions(columns) {
  const out = [{ value: '__count__', label: '(Record count)' }];
  const walk = (cols) => {
    for (const c of cols || []) {
      if (Array.isArray(c.children) && c.children.length) {
        walk(c.children);
        continue;
      }
      if (!c) continue;
      if (c.key === '__erp_actions__') continue;
      const aggregatable = c.aggregatable === true || Boolean(c.aggregate);
      if (!aggregatable) continue;
      const key = c.key || c.dataIndex;
      if (!key) continue;
      const dataIndex = c.dataIndex || c.key;
      out.push({
        value: typeof dataIndex === 'string' ? dataIndex : String(dataIndex),
        label: typeof c.title === 'string' ? c.title : String(key)
      });
    }
  };
  walk(columns);
  return out;
}

// Best-effort default config. If the caller declared at least one
// pivotable column and one aggregatable column we wire those up;
// otherwise the UI shows the empty state.
function buildDefaultConfig(axisOptions, measureOptions) {
  return {
    rows: axisOptions[0] ? [axisOptions[0].value] : [],
    columns: axisOptions[1] ? [axisOptions[1].value] : [],
    values: [
      {
        field: measureOptions[1] ? measureOptions[1].value : measureOptions[0].value,
        aggregate:
          measureOptions[1] && measureOptions[1].value !== '__count__' ? 'sum' : 'count'
      }
    ]
  };
}

/**
 * @param {{
 *   columns: GridColumn[],
 *   rows: any[],
 *   gridId?: string,
 *   title?: string,
 *   description?: string,
 *   maxRows?: number,    // soft warning threshold; defaults to 1000
 *   className?: string
 * }} props
 */
export default function ErpPivotView({
  columns,
  rows,
  gridId,
  title = 'Pivot preview',
  description,
  maxRows = 1000,
  className
}) {
  // Persistence: stash the pivot config under a `:pivot` suffix so
  // it never collides with a parent grid's layout slot. Row data is
  // NEVER passed to setPersisted — only the config fields.
  const pivotPersistenceId = gridId ? `${gridId}:pivot` : undefined;
  const persistence = useErpGridPersistence({ gridId: pivotPersistenceId });
  const persistedState = persistence.state || {};

  const axisOptions = useMemo(() => collectAxisOptions(columns), [columns]);
  const measureOptions = useMemo(() => collectMeasureOptions(columns), [columns]);

  // Initial config — persisted if available, else the auto-default.
  const [config, setConfig] = useState(() => {
    const seed =
      persistedState.pivotConfig && typeof persistedState.pivotConfig === 'object'
        ? persistedState.pivotConfig
        : null;
    if (seed) return seed;
    if (axisOptions.length === 0 || measureOptions.length === 0) {
      return { rows: [], columns: [], values: [] };
    }
    return buildDefaultConfig(axisOptions, measureOptions);
  });

  // Persist whenever the config changes — but only the config, never
  // a copy of `rows`. The persistence hook silently no-ops when the
  // user is anonymous.
  useEffect(() => {
    if (pivotPersistenceId) {
      persistence.setPersisted({ pivotConfig: config });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const setRowField = useCallback((field) => {
    setConfig((c) => ({ ...c, rows: field ? [field] : [] }));
  }, []);
  const setColumnField = useCallback((field) => {
    setConfig((c) => ({ ...c, columns: field ? [field] : [] }));
  }, []);
  const setValueField = useCallback((field) => {
    setConfig((c) => {
      const aggregate = field === '__count__' ? 'count' : c.values?.[0]?.aggregate || 'sum';
      return {
        ...c,
        values: field ? [{ field, aggregate }] : []
      };
    });
  }, []);
  const setAggregate = useCallback((aggregate) => {
    setConfig((c) => ({
      ...c,
      values: (c.values || []).map((m, i) => (i === 0 ? { ...m, aggregate } : m))
    }));
  }, []);

  const safeRows = Array.isArray(rows) ? rows : [];
  const tooManyRows = safeRows.length > maxRows;

  // Build the matrix. Each call is pure and memoised on the
  // (config, rows) tuple so re-renders don't redo work needlessly.
  const matrix = useMemo(() => {
    if (
      !config.rows?.length && !config.columns?.length
    ) {
      return null;
    }
    if (!config.values?.length) return null;
    // For the "(Record count)" measure we count rows regardless of
    // which dataIndex is picked. Build a synthetic measure list so
    // pivotUtils sees a normal config.
    const measures = config.values.map((m) =>
      m.field === '__count__'
        ? { field: '__count__', aggregate: 'count' }
        : { field: m.field, aggregate: m.aggregate || 'sum' }
    );
    return buildPivotMatrix(safeRows, {
      rows: config.rows || [],
      columns: config.columns || [],
      values: measures
    });
  }, [config, safeRows]);

  const currentMeasure = config.values?.[0];
  const currentMeasureKey = currentMeasure
    ? measureKeyOf({ field: currentMeasure.field, aggregate: currentMeasure.aggregate })
    : null;
  const measureLabel = useMemo(() => {
    if (!currentMeasure) return '';
    const opt = measureOptions.find((o) => o.value === currentMeasure.field);
    const left = opt ? opt.label : currentMeasure.field;
    const aggLabel = AGGREGATE_OPTIONS.find((a) => a.value === currentMeasure.aggregate)?.label;
    return aggLabel ? `${aggLabel} of ${left}` : left;
  }, [currentMeasure, measureOptions]);

  // Build AntD Table columns from the pivot matrix. Two surfaces:
  //   1. A leading "row header" column with the row-axis label.
  //   2. One column per column-axis key, each rendering the cell
  //      value for the current single measure. (Multi-measure pivots
  //      can be layered on later — for now the UI is single-measure
  //      to keep the interaction model simple.)
  // Final fixed-right column is the row total.
  const tableColumns = useMemo(() => {
    if (!matrix) return [];
    const rowHeaderTitle =
      (config.rows || []).map((f) => {
        const opt = axisOptions.find((o) => o.value === f);
        return opt ? opt.label : f;
      }).join(' / ') || 'Total';
    const cols = [
      {
        title: rowHeaderTitle,
        dataIndex: '__rowLabel__',
        key: '__rowLabel__',
        width: 220,
        fixed: 'left',
        render: (v) => <span className="font-medium text-ink-800">{v}</span>
      }
    ];
    for (const ck of matrix.columnKeys) {
      cols.push({
        title: ck.label,
        dataIndex: ck.key,
        key: ck.key,
        align: 'right',
        width: 140,
        render: (cell) => {
          const display = formatPivotCell(cell);
          if (display === '—') {
            return <span className="text-ink-300">—</span>;
          }
          return <span className="tabular-nums">{display}</span>;
        }
      });
    }
    cols.push({
      title: 'Total',
      dataIndex: '__rowTotal__',
      key: '__rowTotal__',
      align: 'right',
      width: 140,
      fixed: 'right',
      render: (cell) => {
        const display = formatPivotCell(cell);
        return <span className="font-semibold tabular-nums">{display}</span>;
      }
    });
    return cols;
  }, [matrix, config.rows, axisOptions]);

  // Materialise the rows for the AntD Table. Each row carries the
  // row-axis label, per-column cell values for the active measure,
  // and the per-row total.
  const tableRows = useMemo(() => {
    if (!matrix || !currentMeasureKey) return [];
    const out = [];
    for (const rk of matrix.rowKeys) {
      const inner = matrix.matrix.get(rk.key);
      const rowTotalMap = matrix.totals.row.get(rk.key);
      const row = {
        __key__: rk.key,
        __rowLabel__: rk.label,
        __rowTotal__: rowTotalMap ? rowTotalMap.get(currentMeasureKey) : null
      };
      for (const ck of matrix.columnKeys) {
        const cell = inner ? inner.get(ck.key) : null;
        row[ck.key] = cell ? cell.get(currentMeasureKey) : null;
      }
      out.push(row);
    }
    return out;
  }, [matrix, currentMeasureKey]);

  // Grand-total footer row — sits underneath the table body. Computed
  // from `matrix.totals` so it stays consistent with the cells above.
  const summaryRow = useMemo(() => {
    if (!matrix || !currentMeasureKey) return null;
    const cells = [
      { key: '__rowLabel__', value: 'Grand total', isLabel: true }
    ];
    for (const ck of matrix.columnKeys) {
      const cell = matrix.totals.column.get(ck.key);
      cells.push({
        key: ck.key,
        value: cell ? cell.get(currentMeasureKey) : null,
        isLabel: false
      });
    }
    cells.push({
      key: '__rowTotal__',
      value: matrix.totals.grand.get(currentMeasureKey),
      isLabel: false,
      isGrand: true
    });
    return cells;
  }, [matrix, currentMeasureKey]);

  const valueFieldValue = currentMeasure ? currentMeasure.field : undefined;
  const aggregateValue = currentMeasure ? currentMeasure.aggregate : 'sum';
  const aggregateDisabled = valueFieldValue === '__count__';

  return (
    <Card
      title={
        <Space size={8}>
          <span className="font-semibold text-ink-900">{title}</span>
          <Tag color="processing" className="!font-normal">PREVIEW</Tag>
        </Space>
      }
      className={className}
      bordered
      size="small"
    >
      <Space direction="vertical" size={12} className="w-full">
        {description ? (
          <div className="text-[12.5px] text-ink-600">{description}</div>
        ) : null}

        {/* Selector row */}
        <Space wrap size={12} className="w-full">
          <div className="flex items-center gap-1.5">
            <span className="text-[11.5px] uppercase tracking-wider text-ink-500">Rows</span>
            <Select
              size="small"
              value={config.rows?.[0]}
              onChange={setRowField}
              options={axisOptions}
              allowClear
              placeholder="Pick a row field"
              className="!min-w-[180px]"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11.5px] uppercase tracking-wider text-ink-500">Columns</span>
            <Select
              size="small"
              value={config.columns?.[0]}
              onChange={setColumnField}
              options={axisOptions}
              allowClear
              placeholder="Pick a column field"
              className="!min-w-[180px]"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11.5px] uppercase tracking-wider text-ink-500">Value</span>
            <Select
              size="small"
              value={valueFieldValue}
              onChange={setValueField}
              options={measureOptions}
              placeholder="Pick a measure"
              className="!min-w-[180px]"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11.5px] uppercase tracking-wider text-ink-500">Aggregation</span>
            <Tooltip
              title={aggregateDisabled ? 'Record count uses count aggregation' : ''}
              placement="top"
            >
              <Select
                size="small"
                value={aggregateValue}
                onChange={setAggregate}
                options={AGGREGATE_OPTIONS}
                disabled={aggregateDisabled}
                className="!min-w-[140px]"
              />
            </Tooltip>
          </div>
          <Tooltip
            title={
              <div className="space-y-1">
                <div>{measureLabel || 'Select a measure to start.'}</div>
                <div className="opacity-70">
                  Pivoting {safeRows.length.toLocaleString('en-IN')} {safeRows.length === 1 ? 'row' : 'rows'} in-browser.
                </div>
              </div>
            }
          >
            <InfoCircleOutlined aria-hidden="true" className="text-ink-400" />
          </Tooltip>
        </Space>

        {tooManyRows ? (
          <Alert
            type="warning"
            showIcon
            message={`Pivoting ${safeRows.length.toLocaleString('en-IN')} rows in the browser. For larger datasets prefer server-side aggregation.`}
          />
        ) : null}

        {/* Pivot result */}
        {axisOptions.length === 0 || measureOptions.length <= 1 ? (
          <Empty
            description={
              <span className="text-ink-500">
                Mark grid columns with <code>pivotable: true</code> and
                <code> aggregatable: true</code> to enable the pivot preview.
              </span>
            }
          />
        ) : !matrix || tableRows.length === 0 ? (
          <Empty description="No data to pivot." />
        ) : (
          <Table
            size="small"
            bordered
            rowKey="__key__"
            pagination={false}
            columns={tableColumns}
            dataSource={tableRows}
            scroll={{ x: 'max-content', y: 360 }}
            summary={() =>
              summaryRow ? (
                <Table.Summary.Row>
                  {summaryRow.map((cell) => (
                    <Table.Summary.Cell
                      key={cell.key}
                      index={cell.key}
                      align={cell.isLabel ? 'left' : 'right'}
                      className={cell.isGrand ? 'bg-surface-100' : 'bg-surface-50'}
                    >
                      {cell.isLabel ? (
                        <span className="font-semibold text-ink-800">{cell.value}</span>
                      ) : (
                        <span className={`tabular-nums ${cell.isGrand ? 'font-bold text-ink-900' : 'font-semibold text-ink-800'}`}>
                          {formatPivotCell(cell.value)}
                        </span>
                      )}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              ) : null
            }
          />
        )}
      </Space>
    </Card>
  );
}
